import * as Toggle from '@radix-ui/react-toggle-group'
import { useKestrel } from 'features/kestrelViewer'
import { useInspectionAreaWorkOrders } from 'features/kestrelViewer/hooks/useInspectionAreaWorkOrders'
import { ToggleInspectionAreaVisibilityButton } from 'features/kestrelViewer/kestrelViewerMarkup/inspectionAreas/ToggleInspectionAreaVisibilityButton'
import {
  ActionsContainer,
  DraggableWrapper,
  DropIndicator,
  LeftSideInfo,
  PrimaryLineContainer,
  useDragHandlers,
} from 'features/kestrelViewer/kestrelViewerMarkup/utils/DragAndDropComponents'
import { useEntryBehavior } from 'features/kestrelViewer/kestrelViewerMarkup/utils/useEntryBehavior'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { FC, useState } from 'react'
import styled from 'styled-components'
import { KVInspectionArea } from 'types'

import { DeleteInspectionAreaButton } from './DeleteInspectionAreaButton'
import { EditInspectionAreaButton } from './EditInspectionAreaButton'
import { ViewWorkOrdersButton } from './ViewWorkOrdersButton'
import { ZoomToInspectionAreaButton } from './ZoomToInspectionAreaButton'

const StyledInspectionAreaEntry = styled(Toggle.Item)<{ $selected: boolean }>`
  all: unset;
  cursor: ${({ $selected }) => ($selected ? 'auto' : 'pointer')} !important;
  background-color: ${({ theme, $selected }) =>
    $selected ? theme.forecast.background4 : ''} !important;
  position: relative;
  padding: 4px;

  &:hover {
    opacity: ${({ $selected }) => ($selected ? 0.8 : 1)} !important;
    background-color: ${({ theme, $selected }) =>
      $selected
        ? theme.forecast.background4
        : theme.forecast.background3} !important;
  }
`

const InspectionAreaLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
`

const InspectionAreaStatus = styled.div`
  border-radius: 4px;
  padding: 0 4px;
`

export const InspectionAreaEntry: FC<{
  inspectionArea: KVInspectionArea
  selectedMarkupId: string | undefined
  onReorder?: (
    draggedId: string,
    targetId: string,
    position: 'before' | 'after'
  ) => void
}> = ({ inspectionArea, onReorder }) => {
  const [isHovered, setIsHovered] = useState(false)
  const { kestrelModel, annotations, inspectionAreas } = useKestrel()

  const { workOrdersMap } = useInspectionAreaWorkOrders(
    kestrelModel?.id,
    annotations,
    inspectionAreas
  )
  const workOrderCount = workOrdersMap[inspectionArea.id]?.length || 0

  const { isSelected, handleClick, handleHover, handleHoverEnd } =
    useEntryBehavior(
      inspectionArea,
      KestrelCommands.HIGHLIGHT_INSPECTION_AREA,
      KestrelCommands.UNHIGHLIGHT_ALL_INSPECTION_AREAS,
      'inspectionAreaId'
    )

  const {
    dropPosition,
    isDragging,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragHandlers(
    inspectionArea,
    onReorder || (() => {}),
    'inspectionareaid'
  )

  const statusTextColor =
    inspectionArea.status === 'In Progress' ? 'black' : 'white'
  const statusBgColor =
    inspectionArea.status === 'Not Started'
      ? 'rgb(82, 82, 82)'
      : inspectionArea.status === 'In Progress'
        ? 'rgb(255, 255, 255)'
        : inspectionArea.status === 'Completed'
          ? 'rgb(0, 130, 00)'
          : inspectionArea.status === 'Flagged'
            ? 'rgb(115, 8, 8)'
            : 'rgb(0, 0, 0)'

  return (
    <StyledInspectionAreaEntry
      value={inspectionArea.id}
      $selected={isSelected}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseEnter={() => {
        setIsHovered(true)
        handleHover()
      }}
      onMouseLeave={() => {
        setIsHovered(false)
        handleHoverEnd()
      }}
      onClick={handleClick}>
      {dropPosition && <DropIndicator $position={dropPosition} />}
      <DraggableWrapper
        $isDragging={isDragging}
        draggable={true}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        <PrimaryLineContainer $visible={inspectionArea.visible}>
          <LeftSideInfo>
            <InspectionAreaLabel>{inspectionArea.label}</InspectionAreaLabel>
          </LeftSideInfo>
          {!isHovered && (
            <InspectionAreaStatus
              style={{
                color: statusTextColor,
                backgroundColor: statusBgColor,
              }}>
              {inspectionArea.status}
            </InspectionAreaStatus>
          )}
          {isHovered && (
            <ActionsContainer>
              <EditInspectionAreaButton inspectionArea={inspectionArea} />
              <ZoomToInspectionAreaButton inspectionArea={inspectionArea} />
              <ToggleInspectionAreaVisibilityButton
                inspectionArea={inspectionArea}
              />
              <DeleteInspectionAreaButton
                inspectionAreaId={inspectionArea.id}
              />
            </ActionsContainer>
          )}
        </PrimaryLineContainer>
        {workOrderCount > 0 && !isSelected && (
          <ViewWorkOrdersButton
            inspectionAreaId={inspectionArea.id}
            workOrders={workOrdersMap[inspectionArea.id]}
          />
        )}
        {isSelected && inspectionArea.notes && (
          <div
            style={{
              color: 'lightgray',
              fontStyle: 'italic',
              marginTop: '4px',
            }}>
            {inspectionArea.notes}
          </div>
        )}
      </DraggableWrapper>
    </StyledInspectionAreaEntry>
  )
}
